/**
 * Plain-English METAR / SPECI decoder: every group of one airport report, body and remarks,
 * turned into a line a jumper or a pilot can read without knowing the code.
 *
 * Built for the hangar weather screen, which shows the most recent report in full (Kyle,
 * 2026-10-02: "every single thing on a metar needs to be here in plain data"). It is a
 * presentation layer over `parseMetar`, not a second parser of the numbers:
 *
 * - **The numbers come from `parseMetar`.** Ceiling, prevailing visibility, the tenths-precision
 *   temperature and the report time are read from its result, so the decoded text, the limits
 *   engine and the flight category can never disagree about what the report said.
 * - **The words come from a second walk over the same tokens**, mirroring `parseMetar`'s stage
 *   order exactly (a 4-digit group after the sky groups is garbage, not a visibility). The walk
 *   exists because the `Observation` keeps values, not wording: `P6SM` and `6SM` are both 6 there,
 *   `M1/4SM` is 0.25, runway visual range is dropped. A test checks that both walks leave
 *   exactly the same groups unread, so they cannot drift apart unnoticed.
 * - **Nothing is dropped.** A body group we cannot read becomes a line saying so (and is listed in
 *   `unparsed`); a remark we cannot read is kept with the meaning "Not decoded". A screen that
 *   shows a partial decode as if it were the whole report would be worse than showing the raw text.
 * - **It never throws**, for the same reason `parseMetar` does not: one station's garbled report
 *   must not blank the screen. An unexpected failure returns the raw text with no lines.
 *
 * Units: knots, °C, inHg and statute miles unless `units` says otherwise, with the other unit
 * alongside where pilots expect both (temperature, pressure, metric visibility).
 */

import {
  parseCloudGroup,
  parseMetar,
  parseVisibilityGroup,
  parseWeatherGroup,
  parseWindGroup,
  tokenize,
  type MetarParseResult,
} from './metar.js'
import {
  HPA_PER_INHG,
  METRES_PER_FOOT,
  METRES_PER_STATUTE_MILE,
  cToF,
  convertWind,
  roundForRole,
  roundTo,
  type RoundingRole,
  type WindUnit,
} from './units.js'

// ---------------------------------------------------------------------------------------------
// Public shape. The screen renders exactly this; keep it stable.
// ---------------------------------------------------------------------------------------------

/** FAA flight category, as NOAA's Aviation Weather Center computes `fltCat`. */
export type FlightCategory = 'VFR' | 'MVFR' | 'IFR' | 'LIFR'

/**
 * One element of the report body. `key` is stable for a given kind of element (`wind`,
 * `visibility`, `rvr:16L`, `weather`, `sky`, `temp`, `dewpoint`, `altimeter`, `type`, `time`,
 * `trend`, ...) and unique within one report: a second element of the same kind gets `:2`.
 */
export interface DecodedLine {
  key: string
  label: string
  value: string
}

/** One remark. `code` is the group or groups exactly as written; `meaning` is plain English. */
export interface DecodedRemark {
  code: string
  meaning: string
}

export interface DecodedMetar {
  station: string | null
  raw: string
  /** `null` when the ceiling or the visibility cannot be determined (and the other is not LIFR). */
  flightCategory: FlightCategory | null
  /** Why: "Ceiling 5,500 ft and visibility 10 mi", "Visibility 2 mi (below 3)". */
  flightCategoryReason: string
  /** Every body element, in report order. */
  lines: DecodedLine[]
  /** Every remark group, decoded, in order. Unknown ones have the meaning "Not decoded". */
  remarks: DecodedRemark[]
  /** Body (and trend) groups that matched no known form, exactly as written. */
  unparsed: string[]
}

export interface MetarDecodeUnits {
  wind?: WindUnit | undefined
  temp?: 'C' | 'F' | undefined
  pressure?: 'inHg' | 'hPa' | undefined
  distance?: 'sm' | 'km' | undefined
}

export interface MetarDecodeOptions {
  /** The time the report is being read; see `MetarParseOptions.now`. */
  now: number
  /** IANA time zone for the "(14:55 local)" part of times, e.g. `America/Denver`. */
  timeZone?: string | undefined
  units?: MetarDecodeUnits | undefined
}

/** The meaning given to any remark group we do not recognise. */
export const REMARK_NOT_DECODED = 'Not decoded'

// ---------------------------------------------------------------------------------------------
// Small formatting helpers.
// ---------------------------------------------------------------------------------------------

/** Thousands separators, the way heights and RVR are written on a chart: 5,500. */
function int(n: number): string {
  const r = Math.round(n) + 0
  return String(r).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** A decimal with at most `d` places and no trailing zeros: 2.5, 0.31, 10. */
function dec(n: number, d: number): string {
  const s = (roundTo(n, d) + 0).toFixed(d)
  return d > 0 ? s.replace(/\.?0+$/, '') : s
}

const COMPASS16 = [
  'north', 'north-northeast', 'northeast', 'east-northeast',
  'east', 'east-southeast', 'southeast', 'south-southeast',
  'south', 'south-southwest', 'southwest', 'west-southwest',
  'west', 'west-northwest', 'northwest', 'north-northwest',
] as const

/** "160° (south-southeast)", "070° (east-northeast)": three digits, as a pilot reads a heading. */
function bearing(deg: number): string {
  const name = COMPASS16[Math.round((((deg % 360) + 360) % 360) / 22.5) % 16] ?? ''
  return `${String(deg).padStart(3, '0')}° (${name})`
}

const DIRECTION_WORDS: Record<string, string> = {
  N: 'north', NE: 'northeast', E: 'east', SE: 'southeast',
  S: 'south', SW: 'southwest', W: 'west', NW: 'northwest',
}
const DIR_TOKEN_RE = /^(N|NE|E|SE|S|SW|W|NW)(?:-(N|NE|E|SE|S|SW|W|NW))*$/

/** `SW-NW` → "southwest through northwest". */
function directionWords(tok: string): string {
  return tok
    .split('-')
    .map((d) => DIRECTION_WORDS[d] ?? d)
    .join(' through ')
}

const UNICODE_FRACTION: Record<string, string> = {
  '1/4': '¼', '1/2': '½', '3/4': '¾', '1/8': '⅛', '3/8': '⅜', '5/8': '⅝', '7/8': '⅞',
}

/** "1 1/2" → "1½", "1/16" stays "1/16" (there is no glyph for it). */
function fractionText(whole: number | null, num: number, den: number): string {
  const f = UNICODE_FRACTION[`${num}/${den}`] ?? `${num}/${den}`
  if (whole === null || whole === 0) return f
  return UNICODE_FRACTION[`${num}/${den}`] === undefined ? `${whole} ${f}` : `${whole}${f}`
}

function capitalise(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1)
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'] as const

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

// ---------------------------------------------------------------------------------------------
// Units-aware formatting, bound to one decode call.
// ---------------------------------------------------------------------------------------------

const WIND_LABEL: Record<WindUnit, string> = { kt: 'kt', mph: 'mph', kmh: 'km/h', mps: 'm/s' }
const WIND_DECIMALS: Record<WindUnit, number> = { kt: 0, mph: 0, kmh: 0, mps: 1 }
const REPORT_WIND_UNIT: Record<string, WindUnit> = { KT: 'kt', MPS: 'mps', KMH: 'kmh' }

interface Fmt {
  windUnit: WindUnit
  tempUnit: 'C' | 'F'
  pressureUnit: 'inHg' | 'hPa'
  distanceUnit: 'sm' | 'km'
  /** "14:55", or `null` when no (valid) time zone was given. */
  local: (utc: number) => string | null
}

/**
 * A wind speed as written in the report (`value` in the report's own unit), shown in the site's
 * unit with knots alongside when they differ: "18 mph (16 kt)", the same layout as
 * `formatWind`. Gusts round up, by the rule in units.ts: a converted gust must never be
 * displayed below what was reported.
 */
function windSpeed(f: Fmt, value: number, reportUnit: WindUnit, role: RoundingRole, more: boolean): string {
  const show = (unit: WindUnit): string => {
    const v = unit === reportUnit ? value : roundForRole(convertWind(value, reportUnit, unit), WIND_DECIMALS[unit], role)
    return `${more ? 'more than ' : ''}${dec(v, WIND_DECIMALS[unit])} ${WIND_LABEL[unit]}`
  }
  const primary = show(f.windUnit)
  const extra: string[] = []
  if (f.windUnit !== 'kt') extra.push(show('kt'))
  // A report in m/s or km/h read at a knots site: keep the number the station actually sent.
  if (reportUnit !== 'kt' && reportUnit !== f.windUnit) extra.push(`reported as ${show(reportUnit)}`)
  return extra.length === 0 ? primary : `${primary} (${extra.join(', ')})`
}

/** "28 °C (82 °F)", or the other way round for a °F site. Tenths kept when the value has them. */
function temperature(f: Fmt, c: number): string {
  const d = Number.isInteger(c) ? 0 : 1
  const cs = `${dec(c, d)} °C`
  const fs = `${dec(cToF(c), d)} °F`
  return f.tempUnit === 'C' ? `${cs} (${fs})` : `${fs} (${cs})`
}

/** A temperature difference: a 1 °C spread is a 1.8 °F spread, not 33.8 °F. */
function temperatureDifference(f: Fmt, dc: number): string {
  const d = Number.isInteger(dc) ? 0 : 1
  const cs = `${dec(dc, d)} °C`
  const fs = `${dec((dc * 9) / 5, d)} °F`
  return f.tempUnit === 'C' ? `${cs} (${fs})` : `${fs} (${cs})`
}

function pressureFromInHg(f: Fmt, inHg: number): string {
  const a = `${(roundTo(inHg, 2) + 0).toFixed(2)} inHg`
  // No thousands separator: pressures are written "1013 hPa" everywhere they are quoted.
  const h = `${roundTo(inHg * HPA_PER_INHG, 0) + 0} hPa`
  return f.pressureUnit === 'inHg' ? `${a} (${h})` : `${h} (${a})`
}

function pressureFromHpa(f: Fmt, hpa: number, hpaDecimals: number): string {
  const h = `${dec(hpa, hpaDecimals)} hPa`
  const a = `${(roundTo(hpa / HPA_PER_INHG, 2) + 0).toFixed(2)} inHg`
  return f.pressureUnit === 'hPa' ? `${h} (${a})` : `${a} (${h})`
}

/** A distance in statute miles already written as text ("1½"), with km alongside on a km site. */
function milesText(f: Fmt, text: string, sm: number): string {
  const unit = sm === 1 || text === '1' || /^[¼½¾⅛⅜⅝⅞]$|^\d+\/\d+$/.test(text) ? 'mile' : 'miles'
  const base = `${text} ${unit}`
  if (f.distanceUnit !== 'km') return base
  const km = (sm * METRES_PER_STATUTE_MILE) / 1000
  return `${base} (${dec(km, km < 10 ? 1 : 0)} km)`
}

/** Metres as reported, with statute miles alongside: "4,000 m (2.5 miles)". */
function metresText(m: number): string {
  const sm = m / METRES_PER_STATUTE_MILE
  return `${int(m)} m (${dec(sm, sm < 1 ? 2 : 1)} ${sm === 1 ? 'mile' : 'miles'})`
}

/** Feet with metres alongside on a km site; feet alone otherwise (heights are feet everywhere). */
function feetText(f: Fmt, ft: number): string {
  return f.distanceUnit === 'km' ? `${int(ft)} ft (${int(ft * METRES_PER_FOOT)} m)` : `${int(ft)} ft`
}

// ---------------------------------------------------------------------------------------------
// Present weather.
// ---------------------------------------------------------------------------------------------

const PHENOMENON: Record<string, string> = {
  DZ: 'drizzle', RA: 'rain', SN: 'snow', SG: 'snow grains', IC: 'ice crystals', PL: 'ice pellets',
  GR: 'hail', GS: 'small hail or snow pellets', UP: 'unknown precipitation',
  BR: 'mist', FG: 'fog', FU: 'smoke', VA: 'volcanic ash', DU: 'widespread dust', SA: 'sand',
  HZ: 'haze', PY: 'spray',
  PO: 'dust or sand whirls', SQ: 'squalls', FC: 'funnel cloud', SS: 'sandstorm', DS: 'duststorm',
}
const PRECIPITATION = new Set(['DZ', 'RA', 'SN', 'SG', 'IC', 'PL', 'GR', 'GS', 'UP'])
const WX_PARTS_RE =
  /^(\+|-|VC)?(MI|PR|BC|DR|BL|SH|TS|FZ)?((?:DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PY|PO|SQ|FC|SS|DS)*)$/

function joinAnd(items: string[]): string {
  if (items.length <= 1) return items.join('')
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1] ?? ''}`
}

/**
 * One present-weather group in words. `-SHRA` → "Light rain showers", `+TSRA` → "Thunderstorm
 * with heavy rain", `VCSH` → "Showers in the vicinity", `+FC` → "Tornado or waterspout".
 *
 * With no sign, a group containing precipitation is **moderate** (that is what the code means,
 * and a screen that says only "rain" leaves the reader guessing). In a remark (`INTMNT RA`,
 * `RAB15`) the group names a phenomenon rather than reporting an intensity, so `withModerate`
 * is off there. Returns `null` for anything that is not a weather group.
 */
export function describeWeather(group: string, withModerate = true): string | null {
  if (parseWeatherGroup(group) === null) return null
  const m = WX_PARTS_RE.exec(group)
  if (!m) return null
  const [, sign, descriptor, phen = ''] = m
  const codes = phen.match(/.{2}/g) ?? []
  // Drifting or blowing snow is snow lifted off the ground, not falling (as in parseWeatherGroup),
  // so it has no "moderate" intensity to state.
  const hasPrecip = descriptor !== 'DR' && descriptor !== 'BL' && codes.some((c) => PRECIPITATION.has(c))
  if (codes.length === 1 && codes[0] === 'FC' && sign === '+') return 'Tornado or waterspout'
  const intensity =
    sign === '+' ? 'heavy' : sign === '-' ? 'light' : hasPrecip && withModerate && sign !== 'VC' ? 'moderate' : ''
  const names = codes.map((c) => PHENOMENON[c] ?? c)
  const list = joinAnd(names)
  const withIntensity = (s: string): string => (intensity === '' ? s : `${intensity} ${s}`)
  let text: string
  switch (descriptor) {
    case 'TS':
      text = list === '' ? 'thunderstorm' : `thunderstorm with ${withIntensity(list)}`
      if (list === '' && intensity !== '') text = `${intensity} thunderstorm`
      break
    case 'SH':
      text = list === '' ? 'showers' : withIntensity(`${list} showers`)
      break
    case 'FZ':
      text = withIntensity(`freezing ${list}`)
      break
    case 'MI':
      text = withIntensity(`shallow ${list}`)
      break
    case 'PR':
      text = withIntensity(`partial ${list}`)
      break
    case 'BC':
      text = withIntensity(`patches of ${list}`)
      break
    case 'DR':
      text = withIntensity(`low drifting ${list}`)
      break
    case 'BL':
      text = withIntensity(`blowing ${list}`)
      break
    default:
      text = withIntensity(list)
  }
  // "In the vicinity" is 5 to 10 statute miles from the station: seen, but not at the field.
  if (sign === 'VC') text += ' in the vicinity'
  return capitalise(text.trim())
}

// ---------------------------------------------------------------------------------------------
// Sky condition.
// ---------------------------------------------------------------------------------------------

const COVER_WORDS: Record<string, string> = {
  FEW: 'Few clouds',
  SCT: 'Scattered clouds',
  BKN: 'Broken',
  OVC: 'Overcast',
}
const CLOUD_PARTS_RE = /^(FEW|SCT|BKN|OVC|\/{3})(\d{3}|\/{3})(CB|TCU|\/{3})?$/
const CLOUD_TYPE_WORDS: Record<string, string> = { CB: 'cumulonimbus', TCU: 'towering cumulus' }

interface SkyPart {
  /** The text before any "(…)" annotation. */
  text: string
  /** Annotations, e.g. "cumulonimbus". "ceiling" is added after the walk. */
  notes: string[]
  /** A measured BKN/OVC/VV base, so it can be marked as the ceiling. */
  ceilingBaseFt: number | null
}

/** One sky group in words, or `null` if it is not a sky group. `CAVOK` is handled by the caller. */
function describeSky(f: Fmt, tok: string, auto: boolean): SkyPart | null {
  if (parseCloudGroup(tok) === null) return null
  if (tok === 'CLR') {
    // CLR is only ever sent by an automated station, whose ceilometer looks up to 12,000 ft.
    return { text: 'Clear below 12,000 ft (automated station: no cloud detected up to 12,000 ft)', notes: [], ceilingBaseFt: null }
  }
  if (tok === 'SKC') return { text: 'Sky clear', notes: [], ceilingBaseFt: null }
  if (tok === 'NSC') return { text: 'No significant cloud (none below 5,000 ft, no cumulonimbus or towering cumulus)', notes: [], ceilingBaseFt: null }
  if (tok === 'NCD') {
    return { text: `No cloud detected${auto ? ' (automated station)' : ''}`, notes: [], ceilingBaseFt: null }
  }
  const vv = /^VV(\d{3}|\/{3})$/.exec(tok)
  if (vv) {
    const h = vv[1] ?? ''
    if (h.startsWith('/')) return { text: 'Sky obscured, vertical visibility not measured', notes: [], ceilingBaseFt: null }
    const ft = Number(h) * 100
    return { text: `Sky obscured, vertical visibility ${feetText(f, ft)}`, notes: [], ceilingBaseFt: ft }
  }
  const m = CLOUD_PARTS_RE.exec(tok)
  if (!m) return null
  const [, cover = '', h = '', type] = m
  const notes: string[] = []
  if (type !== undefined && !type.startsWith('/')) notes.push(CLOUD_TYPE_WORDS[type] ?? type)
  if (type !== undefined && type.startsWith('/')) notes.push('cloud type not observed')
  const heightKnown = !h.startsWith('/')
  const ft = heightKnown ? Number(h) * 100 : null
  if (cover.startsWith('/')) {
    // `//////`: an automated station could not observe the sky at all (`///015`: cloud at a
    // known height, amount not measured).
    const text = ft === null ? 'Cloud not observed' : `Cloud amount not measured, at ${feetText(f, ft)}`
    return { text, notes, ceilingBaseFt: null }
  }
  const words = COVER_WORDS[cover] ?? cover
  const text = ft === null ? `${words}, height not measured` : `${words} at ${feetText(f, ft)}`
  const isCeiling = (cover === 'BKN' || cover === 'OVC') && ft !== null
  return { text, notes, ceilingBaseFt: isCeiling ? ft : null }
}

function skyPartText(p: SkyPart): string {
  return p.notes.length === 0 ? p.text : `${p.text} (${p.notes.join(', ')})`
}

// ---------------------------------------------------------------------------------------------
// Visibility.
// ---------------------------------------------------------------------------------------------

interface VisibilityText {
  /** For the line: "More than 6 miles", "4,000 m (2.5 miles)". */
  value: string
  /** For the flight-category reason: "10 mi", "more than 6 mi", "10 km or more". */
  short: string
}

/** The prevailing-visibility group(s) in words. `tokens` is one or two groups (`1 1/2SM`). */
function describeVisibility(f: Fmt, tokens: string[]): VisibilityText {
  const joined = tokens.join(' ')
  if (joined === 'CAVOK') return { value: '10 km or more (CAVOK)', short: '10 km or more' }
  const metres = /^(\d{4})(NDV)?$/.exec(joined)
  if (metres) {
    const m = Number(metres[1])
    const ndv = metres[2] === undefined ? '' : '; the sensor cannot report variation by direction (NDV)'
    if (m === 9999) return { value: `10 km or more${ndv}`, short: '10 km or more' }
    // `0000` is "less than 50 m": the lowest step the code has.
    if (m === 0) return { value: `Less than 50 m${ndv}`, short: 'less than 50 m' }
    // The category thresholds are statute miles, so the reason gives the miles too: 8,000 m is
    // 4.97 mi, which is MVFR, and would look like a mistake without the conversion beside it.
    return { value: `${metresText(m)}${ndv}`, short: `${int(m)} m, ${dec(m / METRES_PER_STATUTE_MILE, 2)} mi` }
  }
  const sm = /^([MP])?(?:(\d{1,3})|(\d) (\d)\/(\d{1,2})|(\d)\/(\d{1,2}))SM$/.exec(joined)
  if (sm) {
    const [, pm, whole, mixW, mixN, mixD, fracN, fracD] = sm
    let text: string
    let value: number
    if (whole !== undefined) {
      text = whole.replace(/^0+(?=\d)/, '')
      value = Number(whole)
    } else if (mixW !== undefined) {
      text = fractionText(Number(mixW), Number(mixN), Number(mixD))
      value = Number(mixW) + Number(mixN) / Number(mixD)
    } else {
      text = fractionText(null, Number(fracN), Number(fracD))
      value = Number(fracN) / Number(fracD)
    }
    const prefix = pm === 'P' ? 'more than ' : pm === 'M' ? 'less than ' : ''
    const unitShort = 'mi'
    return { value: capitalise(prefix + milesText(f, text, value)), short: `${prefix}${text} ${unitShort}` }
  }
  return { value: `Not decoded (${joined})`, short: joined }
}

/**
 * A visibility value written in a remark (`2`, `1/2`, `1 1/2`), already split from `SM`-less
 * text. Returns the text and the miles, or `null`.
 */
function remarkMiles(text: string): { text: string; sm: number } | null {
  const m = /^(?:(\d{1,2})|(\d{1,2}) (\d)\/(\d{1,2})|(\d)\/(\d{1,2}))$/.exec(text)
  if (!m) return null
  const [, whole, mixW, mixN, mixD, fracN, fracD] = m
  if (whole !== undefined) return { text: whole, sm: Number(whole) }
  if (mixW !== undefined) {
    if (Number(mixD) === 0) return null
    return { text: fractionText(Number(mixW), Number(mixN), Number(mixD)), sm: Number(mixW) + Number(mixN) / Number(mixD) }
  }
  if (Number(fracD) === 0) return null
  return { text: fractionText(null, Number(fracN), Number(fracD)), sm: Number(fracN) / Number(fracD) }
}

// ---------------------------------------------------------------------------------------------
// Runway groups.
// ---------------------------------------------------------------------------------------------

/** `R16/2400FT`, `R16L/1800V2400FT`, `R34/P2000U`, `R11/1700D`, `R28L/2600FT/U`. */
const RVR_RE = /^R(\d{1,2}[LCR]?)\/([PM])?(\d{4})(?:V([PM])?(\d{4}))?(FT)?(?:\/?([UDN]))?$/

function runwayName(designator: string): string {
  if (designator === '88') return 'all runways'
  if (designator === '99') return 'the runway last reported (repeated)'
  return `runway ${designator}`
}

function describeRvr(f: Fmt, tok: string): DecodedLine | null {
  const m = RVR_RE.exec(tok)
  if (!m) return null
  const [, rwy = '', p1, v1 = '', p2, v2, ft, trend] = m
  const isFeet = ft !== undefined
  const one = (prefix: string | undefined, v: string): string => {
    const n = Number(v)
    const pre = prefix === 'P' ? 'more than ' : prefix === 'M' ? 'less than ' : ''
    if (isFeet) return `${pre}${f.distanceUnit === 'km' ? `${int(n)} ft (${int(n * METRES_PER_FOOT)} m)` : `${int(n)} ft`}`
    return `${pre}${int(n)} m (${int(n / METRES_PER_FOOT)} ft)`
  }
  let value = v2 === undefined ? one(p1, v1) : `varying from ${one(p1, v1)} to ${one(p2, v2)}`
  const trendWord = trend === 'U' ? 'increasing' : trend === 'D' ? 'decreasing' : trend === 'N' ? 'no change' : null
  if (trendWord !== null) value += `, ${trendWord}`
  return { key: `rvr:${rwy}`, label: `Runway ${rwy} visual range`, value: capitalise(value) }
}

const DEPOSIT = [
  'clear and dry', 'damp', 'wet or water patches', 'rime or frost', 'dry snow',
  'wet snow', 'slush', 'ice', 'compacted or rolled snow', 'frozen ruts or ridges',
]
const EXTENT: Record<string, string> = {
  '1': '10% or less of the runway', '2': '11–25% of the runway', '5': '26–50% of the runway', '9': '51–100% of the runway',
}

function depthText(dd: string): string {
  if (dd === '//') return 'depth not measured'
  const n = Number(dd)
  if (n === 0) return 'depth less than 1 mm'
  if (n >= 1 && n <= 90) return `depth ${n} mm`
  const cm: Record<number, string> = { 92: '10 cm', 93: '15 cm', 94: '20 cm', 95: '25 cm', 96: '30 cm', 97: '35 cm', 98: '40 cm or more' }
  if (cm[n] !== undefined) return `depth ${cm[n]}`
  if (n === 99) return 'runway not operational'
  return `depth code ${dd}`
}

function frictionText(bb: string): string {
  if (bb === '//') return 'braking action not reported'
  const n = Number(bb)
  if (n >= 1 && n <= 90) return `friction coefficient 0.${pad2(n)}`
  const words: Record<number, string> = {
    91: 'braking action poor', 92: 'braking action medium to poor', 93: 'braking action medium',
    94: 'braking action medium to good', 95: 'braking action good', 99: 'braking action unreliable',
  }
  return words[n] ?? `braking code ${bb}`
}

/** Runway state (`R24/290050`, `R06C/CLRD62`, `R88/190050`, or the old 8-digit `24290050`). */
function describeRunwayState(tok: string): DecodedLine | null {
  let rwy: string
  let body: string
  const r = /^R(\d{1,2}[LCR]?)\/(.+)$/.exec(tok)
  if (r) {
    rwy = r[1] ?? ''
    body = r[2] ?? ''
  } else if (/^\d{8}$/.test(tok)) {
    // Old form: 51-86 is the right-hand parallel of runway 01-36.
    const d = Number(tok.slice(0, 2))
    rwy = d > 50 && d < 87 ? `${pad2(d - 50)}R` : pad2(d)
    body = tok.slice(2)
  } else return null
  const label = `${capitalise(runwayName(rwy))} state`
  const key = `runway-state:${rwy}`
  const clrd = /^CLRD(\d{2}|\/\/)$/.exec(body)
  if (clrd) return { key, label, value: `Contamination cleared, ${frictionText(clrd[1] ?? '//')}` }
  if (body === 'SNOCLO') return { key, label, value: 'Aerodrome closed by snow' }
  // All slashes: the state group was sent with nothing in it.
  if (/^\/+$/.test(body)) return { key, label, value: 'Not reported' }
  const s = /^([\d/])([\d/])(\d{2}|\/\/)(\d{2}|\/\/)$/.exec(body)
  if (!s) return { key, label, value: `Not decoded (${tok})` }
  const [, e = '/', c = '/', dd = '//', bb = '//'] = s
  const deposit = e === '/' ? 'deposit not reported' : (DEPOSIT[Number(e)] ?? `deposit code ${e}`)
  // A clear and dry runway has nothing to cover it or measure the depth of; some stations still
  // send an extent of 0 and a depth of 00, which would read as nonsense if spelled out.
  if (e === '0') return { key, label, value: capitalise(`${deposit}, ${frictionText(bb)}`) }
  const extent = c === '/' ? 'extent not reported' : (EXTENT[c] ?? `extent code ${c}`)
  return { key, label, value: capitalise(`${deposit}, covering ${extent}, ${depthText(dd)}, ${frictionText(bb)}`) }
}

// ---------------------------------------------------------------------------------------------
// Other body groups.
// ---------------------------------------------------------------------------------------------

const STATION_RE = /^[A-Z][A-Z0-9]{3}$/
const TIME_RE = /^(\d{2})(\d{2})(\d{2})Z$/
const WIND_PARTS_RE = /^(\d{3}|VRB|\/{3})(P?\d{2,3}|\/\/)(?:G(P?\d{2,3}))?(KT|MPS|KMH)$/
const VAR_DIR_RE = /^(\d{3})V(\d{3})$/
const TEMP_RE = /^(M?\d{2}|\/\/)\/(M?\d{2}|\/\/)?$/
const ALT_A_RE = /^A(\d{4}|\/{4})$/
const ALT_Q_RE = /^Q(\d{4}|\/{4})$/
const RUNWAY_RE = /^R\d{1,2}[LCR]?\/\S+$/
const DIR_VIS_RE = /^(\d{4})(N|NE|E|SE|S|SW|W|NW)$/
const SEA_RE = /^W(M?\d{2}|\/\/)\/(S[\d/]|H[\d/]+)$/
const COLOUR_RE = /^(BLACK)?(BLU|WHT|GRN|YLO\d?|AMB|RED)\+?$/
const RUNWAY_STATE_8_RE = /^\d{8}$/
const NOT_A_STATION = new Set(['AUTO', 'NIL', 'COR', 'METAR', 'SPECI', 'RMK', 'TEMPO', 'BECMG', 'NOSIG'])
const Stage = { Wind: 0, Visibility: 1, Weather: 2, Sky: 3, Temp: 4, Pressure: 5 } as const

function describeWind(f: Fmt, tok: string): string | null {
  if (parseWindGroup(tok) === null) return null
  const m = WIND_PARTS_RE.exec(tok)
  if (!m) return null
  const [, dir = '', spd = '', gst, unitCode = 'KT'] = m
  const unit = REPORT_WIND_UNIT[unitCode] ?? 'kt'
  if (spd.startsWith('/')) return dir.startsWith('/') ? 'Not measured' : `From ${bearing(Number(dir))}, speed not measured`
  const speed = Number(spd.replace('P', ''))
  const more = spd.startsWith('P')
  if (speed === 0 && gst === undefined && (dir === '000' || dir === 'VRB' || dir.startsWith('/'))) return 'Calm'
  const speedText = windSpeed(f, speed, unit, 'average', more)
  let text: string
  if (dir === 'VRB') text = `Variable direction at ${speedText}`
  else if (dir.startsWith('/')) text = `Direction not measured, ${speedText}`
  else {
    const d = Number(dir)
    // `000` with a speed is north written oddly; see parseWindGroup.
    text = `${bearing(d === 0 ? 360 : d)} at ${speedText}`
  }
  if (gst !== undefined) text += `, gusting ${windSpeed(f, Number(gst.replace('P', '')), unit, 'gust', gst.startsWith('P'))}`
  return text
}

const COLOUR_WORDS: Record<string, string> = {
  BLU: 'blue: cloud base 2,500 ft or more and visibility 8 km or more',
  WHT: 'white: cloud base 1,500 ft or more and visibility 5 km or more',
  GRN: 'green: cloud base 700 ft or more and visibility 3.7 km or more',
  YLO: 'yellow: cloud base 300 ft or more and visibility 1,600 m or more',
  YLO1: 'yellow 1: cloud base 500 ft or more and visibility 2,500 m or more',
  YLO2: 'yellow 2: cloud base 300 ft or more and visibility 1,600 m or more',
  AMB: 'amber: cloud base 200 ft or more and visibility 800 m or more',
  RED: 'red: cloud base below 200 ft or visibility below 800 m',
}

/** Military airfield colour state: a summary of the cloud and visibility already reported. */
function describeColour(tok: string): string | null {
  const m = COLOUR_RE.exec(tok)
  if (!m) return null
  const [, black, colour = ''] = m
  let text = `Military colour state ${COLOUR_WORDS[colour] ?? colour}`
  if (black !== undefined) text += '; BLACK: airfield unusable for reasons other than weather'
  if (tok.endsWith('+')) text += ' (and better expected)'
  return text
}

const SEA_STATE = [
  'calm (glassy)', 'calm (rippled)', 'smooth (wavelets)', 'slight', 'moderate',
  'rough', 'very rough', 'high', 'very high', 'phenomenal',
]

function describeSea(f: Fmt, tok: string): string | null {
  const m = SEA_RE.exec(tok)
  if (!m) return null
  const [, t = '//', s = ''] = m
  const temp = t.startsWith('/') ? 'sea surface temperature not measured' : `sea surface ${temperature(f, t.startsWith('M') ? -Number(t.slice(1)) : Number(t))}`
  let state: string
  if (s.startsWith('S')) {
    const code = s.slice(1)
    state = code === '/' ? 'state of the sea not reported' : `sea ${SEA_STATE[Number(code)] ?? `state ${code}`}`
  } else {
    const code = s.slice(1)
    state = code.includes('/') ? 'wave height not reported' : `significant wave height ${dec(Number(code) / 10, 1)} m`
  }
  return capitalise(`${temp}, ${state}`)
}

// ---------------------------------------------------------------------------------------------
// Trend (non-US reports): NOSIG, BECMG, TEMPO.
// ---------------------------------------------------------------------------------------------

function describeTrend(f: Fmt, tokens: string[], unknown: string[]): string {
  const head = tokens[0] ?? ''
  if (head === 'NOSIG') {
    const rest = tokens.slice(1)
    if (rest.length > 0) unknown.push(...rest)
    return `No significant change expected in the next 2 hours${rest.length > 0 ? ` (then: ${rest.join(' ')}, not decoded)` : ''}`
  }
  const lead = head === 'BECMG' ? 'Becoming, within the next 2 hours' : 'Temporarily, within the next 2 hours'
  const parts: string[] = []
  for (let i = 1; i < tokens.length; i++) {
    const t = tokens[i] ?? ''
    const time = /^(FM|TL|AT)(\d{2})(\d{2})$/.exec(t)
    if (time) {
      const word = time[1] === 'FM' ? 'from' : time[1] === 'TL' ? 'until' : 'at'
      parts.push(`${word} ${time[2] ?? ''}:${time[3] ?? ''}Z`)
      continue
    }
    if (t === 'NSW') {
      parts.push('no significant weather')
      continue
    }
    if (t === 'CAVOK') {
      parts.push('ceiling and visibility OK (CAVOK)')
      continue
    }
    const wind = describeWind(f, t)
    if (wind !== null) {
      parts.push(`wind ${wind.charAt(0).toLowerCase()}${wind.slice(1)}`)
      continue
    }
    const vis = parseVisibilityGroup(t, tokens[i + 1])
    if (vis) {
      const used = tokens.slice(i, i + vis.consumed)
      parts.push(`visibility ${describeVisibility(f, used).value.replace(/^./, (c) => c.toLowerCase())}`)
      i += vis.consumed - 1
      continue
    }
    const wx = describeWeather(t)
    if (wx !== null) {
      parts.push(wx.charAt(0).toLowerCase() + wx.slice(1))
      continue
    }
    const sky = describeSky(f, t, false)
    if (sky !== null) {
      const s = skyPartText(sky)
      parts.push(s.charAt(0).toLowerCase() + s.slice(1))
      continue
    }
    unknown.push(t)
    parts.push(`${t} (not decoded)`)
  }
  return parts.length === 0 ? lead : `${lead}: ${parts.join(', ')}`
}

// ---------------------------------------------------------------------------------------------
// Remarks.
// ---------------------------------------------------------------------------------------------

/** Context a remark needs: the observation's own time, to complete `RAB15` and `/2353`. */
interface RemarkContext {
  f: Fmt
  /** Observation time, UTC ms, or `null` when the report time was not read. */
  observedAt: number | null
}

/**
 * A time written in a remark, `hhmm` or just `mm`. A minutes-only time is in the observation's
 * own hour (that is the code's rule); if those minutes are later than the observation's, the
 * event must have been in the hour before, which happens on reports issued just after the hour.
 */
function remarkClock(ctx: RemarkContext, hh: number | null, mm: number): string {
  if (ctx.observedAt === null) return hh === null ? `${pad2(mm)} minutes past the hour` : `${pad2(hh)}:${pad2(mm)}Z`
  const obs = new Date(ctx.observedAt)
  let t: number
  if (hh === null) {
    t = Date.UTC(obs.getUTCFullYear(), obs.getUTCMonth(), obs.getUTCDate(), obs.getUTCHours(), mm)
    if (mm > obs.getUTCMinutes()) t -= 3_600_000
  } else {
    t = Date.UTC(obs.getUTCFullYear(), obs.getUTCMonth(), obs.getUTCDate(), hh, mm)
    if (t > ctx.observedAt + 60_000) t -= 86_400_000
  }
  const d = new Date(t)
  const z = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}Z`
  const local = ctx.f.local(t)
  return local === null ? z : `${z} (${local} local)`
}

function inchesText(inches: number): string {
  return `${dec(inches, 2)} in (${dec(inches * 25.4, 1)} mm)`
}

/** Signed tenths of a degree, as the remark groups write them: sign digit then three digits. */
function tenths(sign: string, digits: string): number {
  const v = Number(digits) / 10
  return sign === '1' ? -v + 0 : v
}

const TENDENCY = [
  'increasing, then decreasing',
  'increasing, then steady, or increasing more slowly',
  'increasing steadily or unsteadily',
  'decreasing or steady then increasing, or increasing more rapidly',
  'steady',
  'decreasing, then increasing',
  'decreasing, then steady, or decreasing more slowly',
  'decreasing steadily or unsteadily',
  'steady or increasing then decreasing, or decreasing more rapidly',
]

const LTG_TYPES: Record<string, string> = { IC: 'in-cloud', CC: 'cloud-to-cloud', CG: 'cloud-to-ground', CA: 'cloud-to-air' }
const FREQUENCY: Record<string, string> = {
  OCNL: 'Occasional (less than 1 a minute)',
  FRQ: 'Frequent (1 to 6 a minute)',
  CONS: 'Continuous (more than 6 a minute)',
}

const CLOUD_GENUS: Record<string, string> = {
  CI: 'cirrus', CS: 'cirrostratus', CC: 'cirrocumulus', AS: 'altostratus', AC: 'altocumulus',
  ACC: 'altocumulus castellanus', NS: 'nimbostratus', SC: 'stratocumulus', ST: 'stratus',
  SF: 'stratus fractus', CU: 'cumulus', CF: 'cumulus fractus', TCU: 'towering cumulus',
  CB: 'cumulonimbus', FG: 'fog', FU: 'smoke', SN: 'snow', BLSN: 'blowing snow', BR: 'mist',
  HZ: 'haze', DZ: 'drizzle', RA: 'rain', DU: 'dust', SA: 'sand',
}

const PHENOMENA_REMARK: Record<string, string> = {
  TS: 'Thunderstorm',
  CB: 'Cumulonimbus',
  CBMAM: 'Cumulonimbus mammatus',
  TCU: 'Towering cumulus',
  ACC: 'Altocumulus castellanus',
  ACSL: 'Standing lenticular altocumulus',
  SCSL: 'Standing lenticular stratocumulus',
  CCSL: 'Standing lenticular cirrocumulus',
  VIRGA: 'Virga (precipitation falling but not reaching the ground)',
}

/** WMO low (CL), middle (CM) and high (CH) cloud type codes, as in the `8/CLCMCH` remark. */
const CL = [
  'no low cloud', 'fair-weather cumulus', 'towering cumulus', 'cumulonimbus without anvil',
  'stratocumulus from spreading cumulus', 'stratocumulus', 'stratus', 'ragged stratus or cumulus of bad weather',
  'cumulus and stratocumulus at different levels', 'cumulonimbus with anvil',
]
const CM = [
  'no middle cloud', 'thin altostratus', 'thick altostratus or nimbostratus', 'thin altocumulus',
  'patchy altocumulus', 'altocumulus thickening in bands', 'altocumulus from spreading cumulus',
  'altocumulus with altostratus or nimbostratus', 'altocumulus castellanus', 'chaotic altocumulus',
]
const CH = [
  'no high cloud', 'cirrus filaments', 'dense cirrus', 'cirrus from a cumulonimbus', 'cirrus thickening',
  'cirrus and cirrostratus, low in the sky', 'cirrus and cirrostratus, high in the sky',
  'cirrostratus covering the sky', 'cirrostratus not covering the sky', 'cirrocumulus',
]

/** Plain-language contractions used in free-text remarks (FAA JO 7340, ICAO Doc 8400). */
const FREE_TEXT: Record<string, string> = {
  ABV: 'above', AFT: 'after', ALQDS: 'all quadrants', ALQS: 'all quadrants', ALT: 'altitude', AND: 'and',
  BFR: 'before', BINOVC: 'breaks in the overcast', BLO: 'below', BLW: 'below', BTN: 'between', CLD: 'cloud', CLDS: 'clouds',
  COND: 'conditions', CONS: 'continuous', CVCTV: 'convective', DSNT: 'distant', EMBD: 'embedded',
  FROPA: 'frontal passage', FRQ: 'frequent', HR: 'hour', HVY: 'heavy', HZY: 'hazy', ICE: 'ice',
  INTMT: 'intermittent', INTMNT: 'intermittent', ISOL: 'isolated', LAST: 'last', LGT: 'light',
  LWR: 'lower', MISG: 'missing', MOD: 'moderate', MOV: 'moving', MOVD: 'moved', MT: 'mountains',
  MTNS: 'mountains', MTS: 'mountains', NEXT: 'next', OBS: 'observation', OBSC: 'obscured',
  OCNL: 'occasional', OHD: 'overhead', OVHD: 'overhead', OVR: 'over', PAST: 'past', PCPN: 'precipitation',
  QUAD: 'quadrant', RWY: 'runway', SFC: 'surface', STN: 'station', STNRY: 'stationary', TR: 'trace',
  TWR: 'tower', VC: 'in the vicinity', VIS: 'visibility', VLY: 'valley', VRB: 'variable', VRY: 'very',
  VSBY: 'visibility', WND: 'wind',
}

function freeTextWord(tok: string): string | null {
  const w = FREE_TEXT[tok]
  if (w !== undefined) return w
  if (DIR_TOKEN_RE.test(tok)) return directionWords(tok)
  const ord = /^(\d)(ST|ND|RD|TH)$/.exec(tok)
  if (ord) {
    const n = Number(ord[1])
    return ['zeroth', 'first', 'second', 'third', 'fourth'][n] ?? `${n}th`
  }
  const genus = CLOUD_GENUS[tok]
  if (genus !== undefined) return genus
  const wx = describeWeather(tok, false)
  if (wx !== null) return wx.toLowerCase()
  return null
}

/**
 * Location and movement words after a phenomenon (`LTG DSNT W-N`, `CB OHD MOV E`,
 * `TS VC N AND NE MOVD SE`). Returns the phrase (", distant, to the west through north") and how
 * many tokens it used.
 */
function readWhere(r: string[], start: number): { text: string; used: number } {
  const loc: string[] = []
  const dirs: string[] = []
  let move: string | null = null
  let j = start
  while (j < r.length) {
    const t = r[j] ?? ''
    if (t === 'OHD' || t === 'OVHD') loc.push('overhead')
    else if (t === 'VC') loc.push('in the vicinity (5 to 10 miles)')
    else if (t === 'DSNT') loc.push('distant (more than 10 miles)')
    else if (t === 'ALQDS' || t === 'ALQS') loc.push('in all quadrants')
    else if (t === 'OVR' && r[j + 1] === 'STN') {
      loc.push('over the station')
      j++
    } else if (DIR_TOKEN_RE.test(t)) dirs.push(directionWords(t))
    else if (t === 'AND' && (dirs.length > 0 || loc.length > 0) && DIR_TOKEN_RE.test(r[j + 1] ?? '')) {
      // glue between directions: "E AND S"
    } else if ((t === 'MOV' || t === 'MOVD') && DIR_TOKEN_RE.test(r[j + 1] ?? '')) {
      move = `${t === 'MOV' ? 'moving' : 'moved'} ${directionWords(r[j + 1] ?? '')}`
      j += 2
      break
    } else if ((t === 'MOV' || t === 'MOVD') && r[j + 1] === 'UNKN') {
      move = 'movement unknown'
      j += 2
      break
    } else if (t === 'STNRY') {
      move = 'stationary'
      j++
      break
    } else break
    j++
  }
  let text = ''
  if (loc.length > 0) text += `, ${loc.join(', ')}`
  if (dirs.length > 0) text += `, to the ${joinAnd(dirs)}`
  if (move !== null) text += `, ${move}`
  return { text, used: j - start }
}

/** The weather group in a begin/end remark, then its `B`/`E` times: `RAB15E30`, `TSB05`. */
const BEGIN_END_PART_RE =
  /((?:[+-]|VC)?(?:MI|PR|BC|DR|BL|SH|TS|FZ)?(?:DZ|RA|SN|SG|IC|PL|GR|GS|UP|BR|FG|FU|VA|DU|SA|HZ|PY|PO|SQ|FC|SS|DS)*)((?:[BE]\d{2}(?:\d{2})?)+)/gy

function describeBeginEnd(ctx: RemarkContext, tok: string): string | null {
  BEGIN_END_PART_RE.lastIndex = 0
  const out: string[] = []
  let pos = 0
  let m: RegExpExecArray | null
  while (pos < tok.length && (m = BEGIN_END_PART_RE.exec(tok)) !== null && m.index === pos) {
    const [whole, wx = '', times = ''] = m
    if (whole === '' || wx === '') return null
    const name = describeWeather(wx, false)
    if (name === null) return null
    const events: string[] = []
    for (const e of times.match(/[BE]\d{2}(?:\d{2})?/g) ?? []) {
      const digits = e.slice(1)
      const hh = digits.length === 4 ? Number(digits.slice(0, 2)) : null
      const mm = Number(digits.slice(-2))
      if (mm > 59 || (hh !== null && hh > 23)) return null
      events.push(`${e.startsWith('B') ? 'began' : 'ended'} ${remarkClock(ctx, hh, mm)}`)
    }
    out.push(`${name} ${joinAnd(events)}`)
    pos = BEGIN_END_PART_RE.lastIndex
  }
  if (pos !== tok.length || out.length === 0) return null
  return out.join('; ')
}

/** Nearest synoptic hour of the observation, to say whether a `6RRRR` group covers 3 or 6 hours. */
function precipPeriod(observedAt: number | null): string {
  if (observedAt === null) return '3 or 6 hours'
  const h = Math.round(observedAt / 3_600_000) % 24
  if (h % 6 === 0) return '6 hours'
  if (h % 3 === 0) return '3 hours'
  return '3 or 6 hours'
}

type RemarkMatcher = (r: string[], j: number, ctx: RemarkContext) => { meaning: string; used: number } | null

const one = (meaning: string): { meaning: string; used: number } => ({ meaning, used: 1 })

/**
 * Each matcher looks at the remark tokens from position `j` and either declines (`null`) or
 * says what the group means and how many tokens it used. Order matters only where two forms
 * overlap; those cases are commented.
 */
const REMARK_MATCHERS: RemarkMatcher[] = [
  // Station type.
  (r, j) => {
    const t = r[j]
    // `A01` / `A02` are how some offshore platforms write AO1 / AO2; NOAA's feed carries both.
    if (t === 'AO1' || t === 'A01') return one('Automated station without a precipitation sensor (cannot tell rain from snow)')
    if (t === 'AO2' || t === 'A02') return one('Automated station with a precipitation sensor')
    if (t === 'AO1A' || t === 'AO2A') return one(`Automated station ${t === 'AO1A' ? 'without' : 'with'} a precipitation sensor, augmented by a human observer`)
    return null
  },
  // Peak wind: `PK WND 28045/1955` (or `/55`, minutes only, within the hour).
  (r, j, ctx) => {
    if (r[j] !== 'PK' || r[j + 1] !== 'WND') return null
    const v = r[j + 2] ?? ''
    const m = /^(\d{3})(\d{2,3})\/(\d{2})?(\d{2})$/.exec(v)
    if (!m) return { meaning: `Peak wind${v === '' ? '' : ` (value not readable: ${v})`}`, used: v === '' ? 2 : 3 }
    const [, d = '', s = '', hh, mm = ''] = m
    const when = remarkClock(ctx, hh === undefined ? null : Number(hh), Number(mm))
    return { meaning: `Peak wind since the last report: ${bearing(Number(d))} at ${windSpeed(ctx.f, Number(s), 'kt', 'gust', false)}, at ${when}`, used: 3 }
  },
  // Wind shift: `WSHFT 1940` or `WSHFT 40`, optionally `FROPA`.
  (r, j, ctx) => {
    if (r[j] !== 'WSHFT') return null
    const m = /^(\d{2})?(\d{2})$/.exec(r[j + 1] ?? '')
    if (!m) return one('Wind shift')
    const when = remarkClock(ctx, m[1] === undefined ? null : Number(m[1]), Number(m[2]))
    const fropa = r[j + 2] === 'FROPA'
    return { meaning: `Wind shift at ${when}${fropa ? ', caused by a frontal passage' : ''}`, used: fropa ? 3 : 2 }
  },
  // Tower / surface visibility: `TWR VIS 1 1/2`, `SFC VIS 2 1/2`.
  (r, j, ctx) => {
    if ((r[j] !== 'TWR' && r[j] !== 'SFC') || r[j + 1] !== 'VIS') return null
    const who = r[j] === 'TWR' ? 'Tower' : 'Surface'
    for (const n of [2, 1]) {
      const v = remarkMiles(r.slice(j + 2, j + 2 + n).join(' '))
      if (v && r.length >= j + 2 + n) return { meaning: `${who} visibility ${milesText(ctx.f, v.text, v.sm)}`, used: 2 + n }
    }
    return { meaning: `${who} visibility`, used: 2 }
  },
  // Visibility remarks: `VIS 1/2V2`, `VIS 1 1/2V2 1/2`, `VIS NE 2 1/2`, `VIS 2 RWY11`.
  (r, j, ctx) => {
    if (r[j] !== 'VIS') return null
    for (const n of [3, 2, 1]) {
      const text = r.slice(j + 1, j + 1 + n).join(' ')
      if (r.length < j + 1 + n) continue
      const v = /^(.+)V(.+)$/.exec(text)
      if (v) {
        const lo = remarkMiles(v[1] ?? '')
        const hi = remarkMiles(v[2] ?? '')
        if (lo && hi) {
          return { meaning: `Visibility variable between ${lo.text} and ${milesText(ctx.f, hi.text, hi.sm)}`, used: 1 + n }
        }
      }
    }
    const dir = r[j + 1] ?? ''
    if (DIR_TOKEN_RE.test(dir)) {
      for (const n of [2, 1]) {
        const v = remarkMiles(r.slice(j + 2, j + 2 + n).join(' '))
        if (v && r.length >= j + 2 + n) {
          return { meaning: `Visibility ${milesText(ctx.f, v.text, v.sm)} to the ${directionWords(dir)}`, used: 2 + n }
        }
      }
    }
    for (const n of [2, 1]) {
      const v = remarkMiles(r.slice(j + 1, j + 1 + n).join(' '))
      const rwy = /^RWY(\d{2}[LCR]?)$/.exec(r[j + 1 + n] ?? '')
      if (v && rwy && r.length >= j + 1 + n) {
        return { meaning: `Visibility ${milesText(ctx.f, v.text, v.sm)} at runway ${rwy[1] ?? ''}`, used: 2 + n }
      }
    }
    return null
  },
  // Lightning: `[OCNL|FRQ|CONS] LTG[IC][CC][CG][CA] [OHD|VC|DSNT|ALQDS] [dirs]`.
  (r, j) => {
    let k = j
    const freq = FREQUENCY[r[k] ?? '']
    if (freq !== undefined) k++
    const m = /^LTG([ICGA]*)$/.exec(r[k] ?? '')
    if (!m) return null
    const letters = m[1] ?? ''
    let codes: string[]
    if (/^(?:IC|CC|CG|CA)*$/.test(letters)) codes = letters.match(/.{2}/g) ?? []
    else {
      // Written with letters shared between types (`LTGICCCG` for IC, CC and CG), as some
      // stations do: take every type that appears in it.
      codes = ['IC', 'CC', 'CG', 'CA'].filter((c) => letters.includes(c))
      if (codes.length === 0) return null
    }
    const types = codes.map((c) => LTG_TYPES[c] ?? c)
    const where = readWhere(r, k + 1)
    const what = `${types.length > 0 ? `${joinAnd(types)} lightning` : 'Lightning'}`
    const base = freq === undefined ? capitalise(what) : `${freq} ${what.charAt(0).toLowerCase()}${what.slice(1)}`
    return { meaning: `${base}${where.text}`, used: k + 1 - j + where.used }
  },
  // Thunderstorm and significant cloud location/movement: `TS SE MOV NE`, `CB DSNT W`,
  // `ACSL DSNT SE`, `VIRGA SW`. A bare `TS` with no location is still a thunderstorm remark.
  (r, j) => {
    const what = PHENOMENA_REMARK[r[j] ?? '']
    if (what === undefined) return null
    const where = readWhere(r, j + 1)
    return { meaning: `${what}${where.text}`, used: 1 + where.used }
  },
  // Begin / end times of weather: `RAB15E30`, `TSB05`, `RAE2254B21`, `FZRAB10E20SNB20`.
  (r, j, ctx) => {
    const d = describeBeginEnd(ctx, r[j] ?? '')
    return d === null ? null : one(d)
  },
  (r, j) => {
    if (r[j] === 'PRESRR') return one('Pressure rising rapidly')
    if (r[j] === 'PRESFR') return one('Pressure falling rapidly')
    return null
  },
  // Sea-level pressure: `SLP132` is 1013.2 hPa; the leading 9 or 10 is left off.
  (r, j, ctx) => {
    const t = r[j] ?? ''
    if (t === 'SLPNO') return one('Sea-level pressure not available')
    const m = /^SLP(\d{3})$/.exec(t)
    if (!m) return null
    const p = Number(m[1]) / 10
    const hpa = p < 50 ? 1000 + p : 900 + p
    return one(`Sea-level pressure ${pressureFromHpa(ctx.f, hpa, 1)}`)
  },
  // Hourly precipitation: `P0003` is 0.03 in; `P0000` is a trace (too little to measure).
  (r, j) => {
    const m = /^P(\d{4})$/.exec(r[j] ?? '')
    if (!m) return null
    const v = Number(m[1]) / 100
    return one(v === 0 ? 'Trace of precipitation in the last hour (too little to measure)' : `Precipitation in the last hour: ${inchesText(v)}`)
  },
  // 3- or 6-hour (`6RRRR`) and 24-hour (`7RRRR`) precipitation, hundredths of an inch.
  (r, j, ctx) => {
    const m = /^([67])(\d{4}|\/{4})$/.exec(r[j] ?? '')
    if (!m) return null
    const period = m[1] === '7' ? '24 hours' : precipPeriod(ctx.observedAt)
    const raw = m[2] ?? ''
    if (raw.startsWith('/')) return one(`Precipitation in the last ${period}: amount not measured`)
    const v = Number(raw) / 100
    return one(v === 0 ? `Trace of precipitation in the last ${period}` : `Precipitation in the last ${period}: ${inchesText(v)}`)
  },
  // Snow depth `4/sss` (whole inches); snowfall `931sss` and water equivalent `933sss` (tenths).
  (r, j) => {
    const t = r[j] ?? ''
    const sd = /^4\/(\d{3})$/.exec(t)
    if (sd) return one(`Snow depth on the ground: ${Number(sd[1])} in (${dec(Number(sd[1]) * 2.54, 0)} cm)`)
    const sf = /^931(\d{3})$/.exec(t)
    if (sf) return one(`Snowfall in the last 6 hours: ${inchesText(Number(sf[1]) / 10)}`)
    const we = /^933(\d{3})$/.exec(t)
    if (we) return one(`Water equivalent of the snow on the ground: ${inchesText(Number(we[1]) / 10)}`)
    const sun = /^98(\d{3})$/.exec(t)
    if (sun) return one(`Sunshine yesterday: ${Number(sun[1])} minutes`)
    return null
  },
  // Temperature and dew point to a tenth of a degree: `T02110133`.
  (r, j, ctx) => {
    const m = /^T([01])(\d{3})(?:([01])(\d{3}))?$/.exec(r[j] ?? '')
    if (!m) return null
    let text = `Temperature ${temperature(ctx.f, tenths(m[1] ?? '0', m[2] ?? '0'))}`
    if (m[3] !== undefined && m[4] !== undefined) text += `, dew point ${temperature(ctx.f, tenths(m[3], m[4]))}`
    return one(`${text} (to a tenth of a degree)`)
  },
  // 6-hour maximum (`1sTTT`) and minimum (`2sTTT`), and 24-hour maximum/minimum (`4sTTTsTTT`).
  (r, j, ctx) => {
    const t = r[j] ?? ''
    const six = /^([12])([01])(\d{3})$/.exec(t)
    if (six) {
      const v = temperature(ctx.f, tenths(six[2] ?? '0', six[3] ?? '0'))
      return one(`${six[1] === '1' ? 'Highest' : 'Lowest'} temperature in the last 6 hours: ${v}`)
    }
    const day = /^4([01])(\d{3})([01])(\d{3})$/.exec(t)
    if (day) {
      const hi = temperature(ctx.f, tenths(day[1] ?? '0', day[2] ?? '0'))
      const lo = temperature(ctx.f, tenths(day[3] ?? '0', day[4] ?? '0'))
      return one(`Highest and lowest temperature in the last 24 hours: ${hi} and ${lo}`)
    }
    return null
  },
  // 3-hour pressure tendency `5appp`: character `a`, change `ppp` in tenths of a hectopascal.
  (r, j) => {
    const m = /^5([0-8])(\d{3})$/.exec(r[j] ?? '')
    if (!m) return null
    const a = Number(m[1])
    const change = Number(m[2]) / 10
    const inHg = change / HPA_PER_INHG
    const amount = `${dec(change, 1)} hPa (${(roundTo(inHg, 2) + 0).toFixed(2)} inHg)`
    // WMO code 0200: 0-3 end higher than 3 hours ago, 4 the same, 5-8 lower (0 and 5 may also be
    // "the same"; the amount then says so).
    const net = change === 0 || a === 4 ? 'Pressure the same as 3 hours ago' : a < 4 ? `Pressure up ${amount} in the last 3 hours` : `Pressure down ${amount} in the last 3 hours`
    return one(`${net}; ${TENDENCY[a] ?? ''}`)
  },
  // Sensor status.
  (r, j) => {
    const t = r[j] ?? ''
    const flags: Record<string, string> = {
      FZRANO: 'Freezing-rain sensor not working',
      PWINO: 'Precipitation-type sensor not working (rain, snow and the like cannot be identified)',
      PNO: 'Precipitation gauge not working (amounts not available)',
      TSNO: 'Lightning detector not working (thunderstorms may go unreported)',
      RVRNO: 'Runway visual range not available',
      SLPNO: 'Sea-level pressure not available',
    }
    if (flags[t] !== undefined) return one(flags[t])
    if (t === 'CHINO' || t === 'VISNO') {
      const what = t === 'CHINO' ? 'Secondary ceiling sensor' : 'Secondary visibility sensor'
      const loc = r[j + 1] ?? ''
      const rwy = /^RWY(\d{2}[LCR]?)$/.exec(loc)
      if (rwy) return { meaning: `${what} at runway ${rwy[1] ?? ''} not working`, used: 2 }
      if (DIR_TOKEN_RE.test(loc)) return { meaning: `${what} to the ${directionWords(loc)} not working`, used: 2 }
      return one(`${what} not working`)
    }
    if (t === '$') return one('Station needs maintenance (an automated self-check flagged a fault)')
    return null
  },
  // Snow increasing rapidly: `SNINCR 2/10` = 2 in in the last hour, 10 in on the ground.
  (r, j) => {
    if (r[j] !== 'SNINCR') return null
    const m = /^(\d{1,2})\/(\d{1,3})$/.exec(r[j + 1] ?? '')
    if (!m) return one('Snow increasing rapidly')
    return { meaning: `Snow increasing rapidly: ${Number(m[1])} in in the last hour, ${Number(m[2])} in on the ground`, used: 2 }
  },
  // Variable ceiling `CIG 005V010`, or ceiling at a second sensor `CIG 002 RWY11`.
  (r, j, ctx) => {
    if (r[j] !== 'CIG') return null
    const v = /^(\d{3})V(\d{3})$/.exec(r[j + 1] ?? '')
    if (v) {
      return { meaning: `Ceiling variable between ${feetText(ctx.f, Number(v[1]) * 100)} and ${feetText(ctx.f, Number(v[2]) * 100)}`, used: 2 }
    }
    const h = /^(\d{3})$/.exec(r[j + 1] ?? '')
    if (h) {
      const rwy = /^RWY(\d{2}[LCR]?)$/.exec(r[j + 2] ?? '')
      const at = rwy ? ` at runway ${rwy[1] ?? ''}` : ''
      return { meaning: `Ceiling ${feetText(ctx.f, Number(h[1]) * 100)}${at}`, used: rwy ? 3 : 2 }
    }
    return null
  },
  // Variable sky cover: `BKN014 V OVC`, `OVC V BKN`.
  (r, j, ctx) => {
    const a = /^(FEW|SCT|BKN|OVC)(\d{3})?$/.exec(r[j] ?? '')
    const b = /^(FEW|SCT|BKN|OVC)$/.exec(r[j + 2] ?? '')
    if (!a || r[j + 1] !== 'V' || !b) return null
    const word = (c: string): string => ({ FEW: 'few', SCT: 'scattered', BKN: 'broken', OVC: 'overcast' })[c] ?? c
    const at = a[2] === undefined ? 'Sky cover' : `Cloud layer at ${feetText(ctx.f, Number(a[2]) * 100)}`
    return { meaning: `${at} varying between ${word(a[1] ?? '')} and ${word(b[1] ?? '')}`, used: 3 }
  },
  // Cloud types by WMO code: `8/96/` (low, middle, high; `/` = hidden by lower cloud).
  (r, j) => {
    const m = /^8\/([\d/])([\d/])([\d/])$/.exec(r[j] ?? '')
    if (!m) return null
    const part = (c: string | undefined, table: string[]): string =>
      c === undefined || c === '/' ? 'not visible (hidden by lower cloud)' : (table[Number(c)] ?? `code ${c}`)
    return one(`Cloud types: low, ${part(m[1], CL)}; middle, ${part(m[2], CM)}; high, ${part(m[3], CH)}`)
  },
  // Canadian cloud-type remark: genus and eighths of sky covered, `SC5AC2`, `FG8`, `CI7`.
  (r, j) => {
    const t = r[j] ?? ''
    if (!/^(?:[A-Z]{2,4}\d)+$/.test(t)) return null
    const parts: string[] = []
    for (const m of t.matchAll(/([A-Z]{2,4})(\d)/g)) {
      const g = CLOUD_GENUS[m[1] ?? '']
      if (g === undefined) return null
      parts.push(`${g} ${m[2] ?? ''}/8 of the sky`)
    }
    return one(`Cloud types: ${parts.join(', ')}`)
  },
  // Density altitude (Canada): `DENSITY ALT 1300FT`.
  (r, j, ctx) => {
    if (r[j] !== 'DENSITY' || r[j + 1] !== 'ALT') return null
    const m = /^(-?\d+)FT$/.exec(r[j + 2] ?? '')
    if (!m) return { meaning: 'Density altitude', used: 2 }
    return { meaning: `Density altitude ${feetText(ctx.f, Number(m[1]))}`, used: 3 }
  },
  // Pressure at aerodrome level (Russia and neighbours): `QFE754/1006` (mmHg / hPa), per runway
  // `R23R/QFE749/0999`; and cloud base in metres, `QBB200`.
  (r, j) => {
    const t = r[j] ?? ''
    const q = /^(?:R(\d{2}[LCR]?)\/)?QFE(\d{3})(?:\/(\d{4}))?$/.exec(t)
    if (q) {
      const at = q[1] === undefined ? '' : ` at runway ${q[1]}`
      const hpa = q[3] === undefined ? '' : ` (${Number(q[3])} hPa)`
      return one(`Pressure at aerodrome level (QFE)${at}: ${Number(q[2])} mmHg${hpa}`)
    }
    const b = /^QBB(\d{3})$/.exec(t)
    if (b) return one(`Cloud base ${Number(b[1])} m (${int(Number(b[1]) / METRES_PER_FOOT)} ft)`)
    return null
  },
  // Last observation of the day: `LASTOBS/NEXT021100UTC`, or plain `LAST`.
  (r, j) => {
    const t = r[j] ?? ''
    const m = /^LASTOBS\/NEXT(\d{2})(\d{2})(\d{2})UTC$/.exec(t)
    if (m) return one(`Last observation until the station reopens; next on day ${Number(m[1])} at ${m[2] ?? ''}:${m[3] ?? ''}Z`)
    if (t === 'LAST') return one('Last observation until the station reopens')
    return null
  },
  // Military colour state, sometimes put in the remarks.
  (r, j) => {
    const c = describeColour(r[j] ?? '')
    return c === null ? null : one(c)
  },
]

/** Decode the remark section, every token accounted for. */
function decodeRemarks(text: string, ctx: RemarkContext): DecodedRemark[] {
  const r = text.split(/\s+/).filter((t) => t !== '')
  const out: DecodedRemark[] = []
  let free: { codes: string[]; words: string[] } | null = null
  let unknown: string[] = []
  const flush = (): void => {
    if (free !== null) {
      out.push({ code: free.codes.join(' '), meaning: capitalise(free.words.join(' ')) })
      free = null
    }
    if (unknown.length > 0) {
      out.push({ code: unknown.join(' '), meaning: REMARK_NOT_DECODED })
      unknown = []
    }
  }
  let j = 0
  while (j < r.length) {
    let matched: { meaning: string; used: number } | null = null
    for (const m of REMARK_MATCHERS) {
      matched = m(r, j, ctx)
      if (matched !== null) break
    }
    if (matched !== null && matched.used > 0) {
      flush()
      const used = Math.min(matched.used, r.length - j)
      out.push({ code: r.slice(j, j + used).join(' '), meaning: matched.meaning })
      j += used
      continue
    }
    const tok = r[j] ?? ''
    // Plain-language remarks ("VSBY LWR SW", "PCPN VRY LGT") are written in standard
    // contractions; a run of them is expanded word by word into one remark.
    const word = freeTextWord(tok)
    if (word !== null) {
      if (unknown.length > 0) flush()
      if (free === null) free = { codes: [], words: [] }
      free.codes.push(tok)
      free.words.push(word)
    } else {
      if (free !== null) flush()
      unknown.push(tok)
    }
    j++
  }
  flush()
  return out
}

// ---------------------------------------------------------------------------------------------
// Flight category.
// ---------------------------------------------------------------------------------------------

const RANK: Record<FlightCategory, number> = { LIFR: 0, IFR: 1, MVFR: 2, VFR: 3 }

function ceilingCategory(ft: number): FlightCategory {
  if (ft < 500) return 'LIFR'
  if (ft < 1000) return 'IFR'
  if (ft <= 3000) return 'MVFR'
  return 'VFR'
}

function visibilityCategory(sm: number): FlightCategory {
  if (sm < 1) return 'LIFR'
  if (sm < 3) return 'IFR'
  if (sm <= 5) return 'MVFR'
  return 'VFR'
}

const BELOW: Record<FlightCategory, { ceiling: string; visibility: string }> = {
  LIFR: { ceiling: 'below 500', visibility: 'below 1' },
  IFR: { ceiling: 'below 1,000', visibility: 'below 3' },
  MVFR: { ceiling: '3,000 or below', visibility: '5 or below' },
  VFR: { ceiling: '', visibility: '' },
}

/**
 * FAA flight category: the worse of the ceiling's and the visibility's. No BKN/OVC/VV layer is an
 * unlimited ceiling. If one of the two is unknown the category is unknown too, **unless** the
 * known one is already LIFR: nothing the unknown one could be would make it better, and a screen
 * that hides "LIFR" because a ceilometer is out would hide the one category that matters most.
 */
function flightCategoryOf(
  parsed: MetarParseResult,
  visShort: string | null,
  skyReported: boolean,
): { category: FlightCategory | null; reason: string } {
  if (parsed.nil) return { category: null, reason: 'No report (NIL): nothing to categorise' }
  const o = parsed.observation
  const vis = o.visibilitySm
  const ceiling = o.ceilingFtAgl
  const visText = visShort ?? (vis === null ? '' : `${dec(vis, 2)} mi`)
  const vCat = vis === null ? null : visibilityCategory(vis)
  const cCat = ceiling === null ? null : ceiling === 'none' ? 'VFR' : ceilingCategory(ceiling)
  const ceilingUnknownText = skyReported ? 'ceiling height not measured' : 'no sky condition reported'

  if (vCat === null || cCat === null) {
    const known = vCat ?? cCat
    if (known === 'LIFR') {
      const why =
        vCat === 'LIFR' ? `Visibility ${visText} (below 1); ${ceilingUnknownText}` : `Ceiling ${int(ceiling as number)} ft (below 500); visibility not reported`
      return { category: 'LIFR', reason: why }
    }
    const missing: string[] = []
    if (vCat === null) missing.push('visibility not reported')
    if (cCat === null) missing.push(ceilingUnknownText)
    return { category: null, reason: capitalise(joinAnd(missing)) }
  }

  const category = RANK[vCat] <= RANK[cCat] ? vCat : cCat
  const ceilingText = ceiling === 'none' ? 'No ceiling' : `Ceiling ${int(ceiling as number)} ft`
  if (category === 'VFR') return { category, reason: `${ceilingText} and visibility ${visText}` }
  const reasons: string[] = []
  if (cCat === category) reasons.push(`${ceilingText} (${BELOW[category].ceiling})`)
  if (vCat === category) reasons.push(`${reasons.length === 0 ? 'Visibility' : 'visibility'} ${visText} (${BELOW[category].visibility})`)
  return { category, reason: reasons.join(' and ') }
}

// ---------------------------------------------------------------------------------------------
// The decoder.
// ---------------------------------------------------------------------------------------------

function makeFmt(options: MetarDecodeOptions): Fmt {
  const u = options.units ?? {}
  let formatter: Intl.DateTimeFormat | null = null
  if (typeof options.timeZone === 'string' && options.timeZone !== '') {
    try {
      formatter = new Intl.DateTimeFormat('en-GB', {
        timeZone: options.timeZone,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      })
    } catch {
      // An unknown zone name throws RangeError. Show UTC only rather than fail the decode.
      formatter = null
    }
  }
  return {
    windUnit: u.wind ?? 'kt',
    tempUnit: u.temp ?? 'C',
    pressureUnit: u.pressure ?? 'inHg',
    distanceUnit: u.distance ?? 'sm',
    local: (utc) => {
      if (formatter === null || !Number.isFinite(utc)) return null
      try {
        return formatter.format(new Date(utc))
      } catch {
        return null
      }
    },
  }
}

/**
 * Decode one METAR or SPECI into plain English. Pure, and never throws.
 *
 * `options.now` is the time the report is being read (pass the fetch time), used as in
 * `parseMetar` to place the report's day-of-month in a month and year.
 */
export function decodeMetar(raw: string, options: MetarDecodeOptions): DecodedMetar {
  const text = typeof raw === 'string' ? raw.replace(/\r/g, '').trim() : ''
  try {
    return decode(text, options)
  } catch {
    // Not expected: every path is written not to throw. If one does, the screen still gets the
    // raw report, which is the one thing that is never wrong.
    return {
      station: null,
      raw: text,
      flightCategory: null,
      flightCategoryReason: 'Report could not be decoded',
      lines: [],
      remarks: [],
      unparsed: text === '' ? [] : [text],
    }
  }
}

function decode(text: string, options: MetarDecodeOptions): DecodedMetar {
  const f = makeFmt(options)
  const parsed = parseMetar(text, { now: options.now })
  const o = parsed.observation
  const tokens = tokenize(text)
  const lines: DecodedLine[] = []
  const unparsed: string[] = []
  const keyCount = new Map<string, number>()
  const add = (key: string, label: string, value: string): DecodedLine => {
    const n = (keyCount.get(key) ?? 0) + 1
    keyCount.set(key, n)
    const line = { key: n === 1 ? key : `${key}:${n}`, label, value }
    lines.push(line)
    return line
  }
  const unknownGroup = (tok: string): void => {
    unparsed.push(tok)
    add('unknown', 'Not decoded', tok)
  }

  // Header: [METAR|SPECI] [COR] STATION DDHHMMZ. Same order and tests as parseMetar.
  let i = 0
  if (tokens[i] === 'METAR') {
    add('type', 'Report type', 'Routine weather report (METAR)')
    i++
  } else if (tokens[i] === 'SPECI') {
    add('type', 'Report type', 'Special weather report (SPECI), issued between routine reports because conditions changed significantly')
    i++
  }
  if (tokens[i] === 'COR') {
    add('correction', 'Correction', 'Corrected report (COR): replaces an earlier report with an error')
    i++
  }
  const maybeStation = tokens[i]
  if (
    maybeStation !== undefined &&
    STATION_RE.test(maybeStation) &&
    !NOT_A_STATION.has(maybeStation) &&
    parseWeatherGroup(maybeStation) === null &&
    parseCloudGroup(maybeStation) === null
  ) {
    add('station', 'Station', maybeStation)
    i++
  }
  const tm = TIME_RE.exec(tokens[i] ?? '')
  if (tm) {
    if (parsed.timeKnown) {
      const d = new Date(o.observedAt)
      const z = `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}Z`
      const local = f.local(o.observedAt)
      const date = `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()] ?? ''}`
      add('time', 'Observed', `${z}${local === null ? '' : ` (${local} local)`}, ${date}`)
    } else {
      // Already listed in parseMetar's `unparsed`; say so on the screen too.
      unparsed.push(tm[0])
      add('time', 'Observed', `Not readable (${tm[0]})`)
    }
    i++
  }

  let stage: number = Stage.Wind
  let windSeen = false
  let visSeen = false
  let windLine: DecodedLine | null = null
  let visShort: string | null = null
  let skyReported = false
  const skyParts: SkyPart[] = []
  let skyLine: DecodedLine | null = null
  let weatherLine: DecodedLine | null = null
  let lastKind = ''
  let trendStart = -1
  let remarksText: string | null = null

  for (; i < tokens.length; i++) {
    const tok = tokens[i] ?? ''
    const kind = lastKind
    lastKind = ''
    if (tok === 'RMK') {
      remarksText = tokens.slice(i + 1).join(' ')
      break
    }
    if (tok === 'NOSIG' || tok === 'TEMPO' || tok === 'BECMG') {
      trendStart = i
      break
    }
    if (tok === 'AUTO') {
      add('auto', 'Automated', 'Fully automated report, no human observer')
      continue
    }
    if (tok === 'COR' || /^CC[A-Z]$/.test(tok)) {
      add('correction', 'Correction', `Corrected report (${tok}): replaces an earlier report with an error`)
      continue
    }
    if (tok === 'NIL') {
      add('nil', 'Missing', 'No report (NIL): the station sent an empty report')
      continue
    }
    if (tok === 'RTD') {
      add('delayed', 'Delayed', 'Routine report sent late (RTD)')
      continue
    }
    if (tok === '$') {
      add('maintenance', 'Maintenance', 'Station needs maintenance (an automated self-check flagged a fault)')
      continue
    }

    if (!windSeen && stage <= Stage.Wind && parseWindGroup(tok) !== null) {
      windSeen = true
      windLine = add('wind', 'Wind', describeWind(f, tok) ?? tok)
      stage = Stage.Wind
      continue
    }
    const vd = VAR_DIR_RE.exec(tok)
    if (vd && stage <= Stage.Visibility) {
      const range = `varying between ${bearing(Number(vd[1]))} and ${bearing(Number(vd[2]))}`
      if (windLine !== null) windLine.value += `; direction ${range}`
      else add('wind-variation', 'Wind direction', capitalise(range))
      continue
    }
    if (!visSeen && stage <= Stage.Visibility) {
      const vis = parseVisibilityGroup(tok, tokens[i + 1])
      if (vis) {
        visSeen = true
        const used = tokens.slice(i, i + vis.consumed)
        const v = describeVisibility(f, used)
        visShort = v.short
        add('visibility', 'Visibility', v.value)
        if (tok === 'CAVOK') {
          // Ceiling and visibility OK: one group standing for three elements.
          add('weather', 'Weather', 'No significant weather (CAVOK)')
          skyReported = true
          add('sky', 'Sky', 'No cloud below 5,000 ft (or the highest minimum sector altitude) and no cumulonimbus or towering cumulus (CAVOK)')
          stage = Stage.Sky
        } else {
          stage = Stage.Visibility
        }
        i += vis.consumed - 1
        continue
      }
    }
    if (tok === '////' && stage <= Stage.Visibility) {
      add('visibility', 'Visibility', 'Not measured')
      stage = Stage.Visibility
      continue
    }
    const dv = DIR_VIS_RE.exec(tok)
    if (dv && stage <= Stage.Visibility) {
      add('visibility-min', 'Minimum visibility', `${metresText(Number(dv[1]))} to the ${DIRECTION_WORDS[dv[2] ?? ''] ?? dv[2] ?? ''}`)
      continue
    }
    if (stage === Stage.Visibility && /^\d{4}$/.test(tok)) {
      add('visibility-min', 'Minimum visibility', `${metresText(Number(tok))}, direction not given`)
      continue
    }
    if (RUNWAY_RE.test(tok) || RUNWAY_STATE_8_RE.test(tok)) {
      const line = describeRvr(f, tok) ?? describeRunwayState(tok) ?? { key: 'runway', label: 'Runway', value: `Not decoded (${tok})` }
      add(line.key, line.label, line.value)
      continue
    }
    if (tok === '//') {
      if (kind === 'weather' && weatherLine !== null) weatherLine.value += '; not observed'
      else weatherLine = add('weather', 'Weather', 'Present weather not observed')
      lastKind = 'weather'
      stage = Math.max(stage, Stage.Weather)
      continue
    }
    if (tok.startsWith('RE') && tok.length > 2 && parseWeatherGroup(tok.slice(2)) !== null) {
      add('recent-weather', 'Recent weather', `${describeWeather(tok.slice(2), false) ?? tok} in the past hour, now ended or eased`)
      continue
    }
    if (stage <= Stage.Sky && parseWeatherGroup(tok) !== null) {
      const w = describeWeather(tok) ?? tok
      if (kind === 'weather' && weatherLine !== null) weatherLine.value += `; ${w}`
      else weatherLine = add('weather', 'Weather', w)
      lastKind = 'weather'
      stage = Math.max(stage, Stage.Weather)
      continue
    }
    if (stage <= Stage.Sky && parseCloudGroup(tok) !== null) {
      skyReported = true
      const part = describeSky(f, tok, parsed.auto) ?? { text: tok, notes: [], ceilingBaseFt: null }
      skyParts.push(part)
      // One line for the whole sky, at the first layer's place. Layers are contiguous in every
      // real report; if one were not, it still belongs with the others, not on a line of its own.
      if (skyLine === null) skyLine = add('sky', 'Sky', '')
      stage = Stage.Sky
      continue
    }
    const t = TEMP_RE.exec(tok)
    if (t && stage <= Stage.Temp) {
      // The tenths from the remark T group, when there is one, are the same reading more exactly.
      const tc = o.tempC
      const dc = o.dewpointC
      add('temp', 'Temperature', tc === null ? 'Not measured' : temperature(f, tc))
      if (dc === null) add('dewpoint', 'Dew point', 'Not measured')
      else {
        const spread = tc === null ? '' : `; spread ${temperatureDifference(f, roundTo(tc - dc, 1))}`
        add('dewpoint', 'Dew point', `${temperature(f, dc)}${spread}`)
      }
      stage = Stage.Temp
      continue
    }
    const a = ALT_A_RE.exec(tok)
    if (a) {
      const v = a[1] ?? ''
      add('altimeter', 'Altimeter', v.startsWith('/') ? 'Not measured' : pressureFromInHg(f, Number(v) / 100))
      stage = Stage.Pressure
      continue
    }
    const q = ALT_Q_RE.exec(tok)
    if (q) {
      const v = q[1] ?? ''
      add('altimeter', 'Altimeter (QNH)', v.startsWith('/') ? 'Not measured' : pressureFromHpa(f, Number(v), 0))
      stage = Stage.Pressure
      continue
    }
    if (tok === 'WS') {
      const runways: string[] = []
      while (/^(R\d{2}[LCR]?|RWY\d{2}[LCR]?|ALL|RWY)$/.test(tokens[i + 1] ?? '')) {
        runways.push(tokens[i + 1] ?? '')
        i++
      }
      const which = runways.join(' ')
      const where =
        which === 'ALL RWY' ? 'on all runways' : runways.length === 0 ? '' : `on runway ${runways.map((r) => r.replace(/^(RWY|R)/, '')).join(', ')}`
      add('wind-shear', 'Wind shear', capitalise(`wind shear ${where}`.trim()))
      continue
    }
    const sea = describeSea(f, tok)
    if (sea !== null) {
      add('sea', 'Sea', sea)
      continue
    }
    const colour = describeColour(tok)
    if (colour !== null) {
      add('colour', 'Colour state', colour)
      continue
    }
    unknownGroup(tok)
  }

  // Sky line: every layer in order, the ceiling marked. The ceiling is parseMetar's, so the
  // mark, the limits engine and the flight category agree on which layer it is.
  if (skyLine !== null) {
    const ceiling = parsed.nil ? null : o.ceilingFtAgl
    let marked = false
    for (const p of skyParts) {
      if (!marked && typeof ceiling === 'number' && p.ceilingBaseFt === ceiling) {
        p.notes.push('ceiling')
        marked = true
      }
    }
    skyLine.value = skyParts.map(skyPartText).join('; ')
  }

  // Trend, up to the remarks.
  if (trendStart >= 0) {
    const rmk = tokens.indexOf('RMK', trendStart)
    const trendTokens = tokens.slice(trendStart, rmk === -1 ? undefined : rmk)
    if (rmk !== -1) remarksText = tokens.slice(rmk + 1).join(' ')
    let seg: string[] = []
    const segments: string[][] = []
    for (const t of trendTokens) {
      if ((t === 'NOSIG' || t === 'TEMPO' || t === 'BECMG') && seg.length > 0) {
        segments.push(seg)
        seg = []
      }
      seg.push(t)
    }
    if (seg.length > 0) segments.push(seg)
    for (const s of segments) add('trend', 'Trend (forecast)', describeTrend(f, s, unparsed))
  }

  const remarks =
    remarksText === null || remarksText.trim() === ''
      ? []
      : decodeRemarks(remarksText, { f, observedAt: parsed.timeKnown ? o.observedAt : null })

  const fc = flightCategoryOf(parsed, visShort, skyReported)
  return {
    station: parsed.station,
    raw: text,
    flightCategory: fc.category,
    flightCategoryReason: fc.reason,
    lines,
    remarks,
    unparsed,
  }
}
